import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { TRANSLATIONS, SUPPORTED_LOCALES, detectLocale } from "../i18n/translations";
import "./LandingV2.css";

const INITIAL_FC = 847293;
const REF_STORAGE_KEY = "cb_ref_code";
const REF_EXPIRY_DAYS = 30;

function readStoredRef() {
  try {
    const raw = localStorage.getItem(REF_STORAGE_KEY);
    if (!raw) return null;
    const { code, exp } = JSON.parse(raw);
    if (!code || !exp || Date.now() > exp) {
      localStorage.removeItem(REF_STORAGE_KEY);
      return null;
    }
    return code;
  } catch (_) { return null; }
}

function persistRef(code) {
  try {
    localStorage.setItem(
      REF_STORAGE_KEY,
      JSON.stringify({ code, exp: Date.now() + REF_EXPIRY_DAYS * 24 * 3600 * 1000 })
    );
  } catch (_) { /* noop */ }
}

export default function LandingV2() {
  const [locale, setLocale] = useState(detectLocale());
  const [fcNum, setFcNum] = useState(INITIAL_FC);
  const [params] = useSearchParams();
  const [refCode, setRefCode] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const particlesRef = useRef(null);
  const rootRef = useRef(null);

  const t = TRANSLATIONS[locale] || TRANSLATIONS.es;

  const changeLocale = useCallback((code) => {
    setLocale(code);
    try { localStorage.setItem("cb_locale", code); } catch (_) { /* noop */ }
  }, []);

  // Capture ?ref= / ?r= and persist
  useEffect(() => {
    const urlRef = (params.get("ref") || params.get("r") || "").trim().toUpperCase();
    if (urlRef) {
      persistRef(urlRef);
      setRefCode(urlRef);
    } else {
      const stored = readStoredRef();
      if (stored) setRefCode(stored);
    }
  }, [params]);

  const registerHref = refCode ? `/register?ref=${encodeURIComponent(refCode)}` : "/register";

  // Update <html lang> and document title on locale change
  useEffect(() => {
    document.documentElement.lang = locale;
    document.title = t.meta_title;
  }, [locale, t.meta_title]);

  // Particles background
  useEffect(() => {
    const c = particlesRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    let W, H, raf;
    const pts = [];

    const resize = () => {
      W = c.width = window.innerWidth;
      H = c.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    for (let i = 0; i < 90; i++) {
      pts.push({
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        r: Math.random() * 1.8 + 0.3,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        a: Math.random(),
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      pts.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = W;
        if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H;
        if (p.y > H) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0,255,136,${p.a * 0.6})`;
        ctx.fill();
      });
      raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  // Floating BC counter
  useEffect(() => {
    const interval = setInterval(() => {
      setFcNum((v) => v + Math.floor(Math.random() * 120 + 20));
    }, 1800);
    return () => clearInterval(interval);
  }, []);

  // Scroll reveal
  useEffect(() => {
    if (!rootRef.current) return;
    const els = rootRef.current.querySelectorAll(".lv2-reveal");
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("visible");
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [locale]);

  const formatNum = (n) => n.toLocaleString(locale === "ru" ? "ru-RU" : locale === "pt-br" ? "pt-BR" : locale === "en" ? "en-US" : "es-ES");

  return (
    <div className="lv2-root" ref={rootRef} data-testid="landing-v2-root">
      <canvas className="lv2-particles" ref={particlesRef} aria-hidden="true" />

      {/* FLOATING BC COUNTER */}
      <div className="lv2-floating-counter" data-testid="lv2-floating-counter">
        <span className="lv2-fc-label">{t.fc_label}</span>
        <span className="lv2-fc-num" data-testid="lv2-fc-num">{formatNum(fcNum)}</span>
        <span className="lv2-fc-sub">{t.fc_sub}</span>
      </div>

      {/* NAV */}
      <nav className="lv2-nav">
        <span className="lv2-nav-logo">💣 CRYPTOBOOM</span>
        <ul className="lv2-nav-links">
          <li><a href="#howtoplay">{t.nav.play}</a></li>
          <li><a href="#earn">{t.nav.earn}</a></li>
          <li><a href="#tasks">{t.nav.tasks}</a></li>
          <li><a href="#leaderboard">{t.nav.rank}</a></li>
          <li><a href="#education">{t.nav.academy}</a></li>
          <li><a href="#nft">{t.nav.nfts}</a></li>
        </ul>
        <div className="lv2-nav-right">
          <div className="lv2-lang" role="group" aria-label="Language selector" data-testid="lv2-lang-switcher">
            {SUPPORTED_LOCALES.map((l) => (
              <button
                key={l.code}
                type="button"
                className={locale === l.code ? "active" : ""}
                onClick={() => changeLocale(l.code)}
                data-testid={`lv2-lang-${l.code}`}
                aria-label={l.label}
                aria-pressed={locale === l.code}
              >
                {l.flag} {l.label}
              </button>
            ))}
          </div>
          <Link to={registerHref} className="lv2-nav-cta" data-testid="lv2-nav-cta">{t.nav.cta}</Link>
        </div>
      </nav>

      {/* TICKER */}
      <div className="lv2-ticker-wrap">
        <div className="lv2-ticker">
          {[...t.ticker, ...t.ticker].map((item, i) => (
            <React.Fragment key={i}>
              <span className="lv2-ticker-item">{item}</span>
              <span className="lv2-ticker-item lv2-ticker-dot">✦</span>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* REFERRAL BANNER */}
      {refCode && !bannerDismissed && (
        <div className="lv2-ref-banner" data-testid="lv2-ref-banner" role="status">
          <span className="lv2-ref-text">
            {t.ref_banner.text_1}
            <strong data-testid="lv2-ref-code">{refCode}</strong>
            {t.ref_banner.text_2}
            <strong className="lv2-ref-bonus">{t.ref_banner.bonus}</strong>
            {t.ref_banner.text_3}
          </span>
          <button
            type="button"
            className="lv2-ref-close"
            onClick={() => setBannerDismissed(true)}
            aria-label={t.ref_banner.close}
            data-testid="lv2-ref-banner-close"
          >
            ✕
          </button>
        </div>
      )}

      {/* HERO */}
      <section className="lv2-hero" id="hero">
        <div className="lv2-hero-grid" />
        <div className="lv2-hero-glow" />

        <div className="lv2-hero-char">
          <img src="/mascot.png" alt="CRYPTOBOOM mascot" />
        </div>

        <span className="lv2-hero-eyebrow">{t.hero.eyebrow}</span>

        <h1 className="lv2-hero-title" data-testid="lv2-hero-title">
          <span className="lv2-crypto">{t.hero.title_line1_a}</span>
          <span className="lv2-boom">{t.hero.title_line1_b}</span>
          <br />
          {t.hero.title_line2}
          <span className="lv2-explode">{t.hero.title_line2_accent}</span>
        </h1>

        <p className="lv2-hero-sub">
          {t.hero.sub_1}<strong>{t.hero.sub_strong_1}</strong>{t.hero.sub_2}<strong>{t.hero.sub_strong_2}</strong>{t.hero.sub_3}
        </p>

        <div className="lv2-hero-btns">
          <Link to={registerHref} className="lv2-btn-primary" data-testid="lv2-hero-cta-primary">{t.hero.cta_primary}</Link>
          <a href="#earn" className="lv2-btn-secondary">{t.hero.cta_secondary}</a>
        </div>
      </section>

      {/* STATS */}
      <div className="lv2-stats lv2-reveal">
        {t.stats.map((s, i) => (
          <div className="lv2-stat" key={i}>
            <span className="lv2-stat-num">{s.num}</span>
            <span className="lv2-stat-label">{s.label}</span>
          </div>
        ))}
      </div>

      {/* HOW TO PLAY */}
      <section className="lv2-howtoplay" id="howtoplay">
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <span className="lv2-label lv2-reveal">{t.howto.label}</span>
          <h2 className="lv2-section-title lv2-reveal">
            {t.howto.title_1}<br /><span className="lv2-accent">{t.howto.title_accent}</span>
          </h2>
          <p className="lv2-section-sub lv2-reveal">{t.howto.sub}</p>

          <div className="lv2-steps-grid">
            {t.howto.steps.map((s, i) => (
              <div className="lv2-step-card lv2-reveal" key={i}>
                <span className="lv2-step-num">{String(i + 1).padStart(2, "0")}</span>
                <span className="lv2-step-icon">{s.icon}</span>
                <div className="lv2-step-title">{s.title}</div>
                <p className="lv2-step-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EARN */}
      <section className="lv2-earn" id="earn">
        <div className="lv2-earn-inner">
          <span className="lv2-label lv2-reveal">{t.earn.label}</span>
          <h2 className="lv2-section-title lv2-reveal">
            {t.earn.title_1}<span className="lv2-accent">{t.earn.title_accent}</span>
          </h2>
          <p className="lv2-section-sub lv2-reveal">{t.earn.sub}</p>

          <div className="lv2-earn-grid">
            <div className="lv2-earn-list lv2-reveal">
              {t.earn.items.map((it, i) => (
                <div className="lv2-earn-item" key={i}>
                  <span className="lv2-earn-icon">{it.icon}</span>
                  <div className="lv2-earn-info">
                    <div className="lv2-earn-name">{it.name}</div>
                    <div className="lv2-earn-detail">{it.detail}</div>
                  </div>
                  <span className={`lv2-earn-reward${it.orange ? " orange" : ""}`}>{it.reward}</span>
                </div>
              ))}
            </div>

            <div className="lv2-conv-box lv2-reveal">
              <div className="lv2-conv-title">{t.earn.conv_title}</div>
              {t.earn.conv_rows.map((r, i) => (
                <div className="lv2-conv-row" key={i}>
                  <span className="lv2-conv-label">{r.label}</span>
                  <span className="lv2-conv-val">{r.val}</span>
                </div>
              ))}
              <div className="lv2-conv-bonus">
                <strong>{t.earn.bonus_strong}</strong>
                {t.earn.bonus_text}
                <strong>{t.earn.bonus_highlight}</strong>
                {t.earn.bonus_tail}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL TASKS */}
      <section className="lv2-tasks" id="tasks">
        <div className="lv2-tasks-inner">
          <span className="lv2-label lv2-reveal">{t.tasks.label}</span>
          <h2 className="lv2-section-title lv2-reveal">
            {t.tasks.title_1}<span className="lv2-accent">{t.tasks.title_accent}</span>
          </h2>
          <p className="lv2-section-sub lv2-reveal">{t.tasks.sub}</p>

          <div className="lv2-tasks-grid">
            {t.tasks.cards.map((c, i) => (
              <div className="lv2-task-card lv2-reveal" key={i}>
                <div className={`lv2-task-platform ${c.col}`}>{c.icon}</div>
                <div className="lv2-task-body">
                  <div className="lv2-task-name">{c.name}</div>
                  <div className="lv2-task-desc">{c.desc}</div>
                  <span className={`lv2-task-badge${c.orange ? " orange" : ""}`}>{c.badge}</span>
                  <div className="lv2-task-freq">{c.freq}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GAMIFICATION */}
      <section className="lv2-gami" id="gamification">
        <div className="lv2-gami-inner">
          <span className="lv2-label lv2-reveal">{t.gami.label}</span>
          <h2 className="lv2-section-title lv2-reveal">
            {t.gami.title_1}<span className="lv2-accent">{t.gami.title_accent}</span>
          </h2>
          <p className="lv2-section-sub lv2-reveal">{t.gami.sub}</p>

          <div className="lv2-gami-grid">
            {t.gami.cards.map((c, i) => (
              <div className="lv2-gami-card lv2-reveal" key={i}>
                <span className="lv2-gami-icon">{c.icon}</span>
                <div className="lv2-gami-title">{c.title}</div>
                <p className="lv2-gami-desc">{c.desc}</p>
                <div className="lv2-gami-highlight">{c.highlight}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STREAKS */}
      <section className="lv2-streaks" id="streaks">
        <div className="lv2-streaks-inner">
          <span className="lv2-label lv2-reveal">{t.streaks.label}</span>
          <h2 className="lv2-section-title lv2-reveal">
            {t.streaks.title_1}<span className="lv2-accent">{t.streaks.title_accent}</span>
          </h2>
          <div className="lv2-streak-timeline lv2-reveal">
            {t.streaks.steps.map((s, i) => (
              <div className="lv2-streak-step" key={i}>
                <span className="lv2-streak-day">{s.day}</span>
                <span className="lv2-streak-reward">{s.reward}</span>
                <div className="lv2-streak-text">{s.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEADERBOARD */}
      <section className="lv2-leaderboard" id="leaderboard">
        <div className="lv2-lb-inner">
          <span className="lv2-label lv2-reveal" style={{ display: "block" }}>{t.leaderboard.label}</span>
          <h2 className="lv2-section-title lv2-reveal">
            {t.leaderboard.title_1}<span className="lv2-accent">{t.leaderboard.title_accent}</span>
          </h2>
          <p className="lv2-section-sub lv2-reveal">{t.leaderboard.sub}</p>

          <div className="lv2-lb-table lv2-reveal">
            <div className="lv2-lb-header">
              <span>{t.leaderboard.h_rank}</span>
              <span>{t.leaderboard.h_player}</span>
              <span>{t.leaderboard.h_score}</span>
              <span>{t.leaderboard.h_bc}</span>
            </div>
            {t.leaderboard.rows.map((r, i) => (
              <div className="lv2-lb-row" key={i}>
                <span className={`lv2-lb-rank ${r.cls}`}>{r.rank}</span>
                <div className="lv2-lb-player">
                  <div className="lv2-lb-avatar" style={r.avStyle ? { background: r.avStyle } : undefined}>{r.av}</div>
                  <div>
                    <div className="lv2-lb-name">{r.name}</div>
                    <div className="lv2-lb-country">{r.country}</div>
                  </div>
                </div>
                <span className="lv2-lb-score">{r.score}</span>
                <span className="lv2-lb-tokens">{r.bc}</span>
              </div>
            ))}
          </div>

          <p className="lv2-lb-cta lv2-reveal">
            {t.leaderboard.cta_text}<a href="#cta">{t.leaderboard.cta_link}</a>
          </p>
        </div>
      </section>

      {/* EDUCATION */}
      <section className="lv2-edu" id="education">
        <div className="lv2-edu-inner">
          <span className="lv2-label lv2-reveal">{t.edu.label}</span>
          <h2 className="lv2-section-title lv2-reveal">
            {t.edu.title_1}<span className="lv2-accent">{t.edu.title_accent}</span>
          </h2>
          <p className="lv2-section-sub lv2-reveal">{t.edu.sub}</p>

          <div className="lv2-edu-grid">
            <div className="lv2-edu-modules lv2-reveal">
              {t.edu.modules.map((m, i) => (
                <div className={`lv2-edu-module${m.orange ? " highlight" : ""}`} key={i}>
                  <span className="lv2-edu-module-icon">{m.icon}</span>
                  <div>
                    <div className="lv2-edu-module-name">{m.name}</div>
                    <div className="lv2-edu-module-meta">{m.meta}</div>
                  </div>
                  <span className={`lv2-edu-module-bc${m.orange ? " orange" : ""}`}>{m.bc}</span>
                </div>
              ))}
            </div>

            <div className="lv2-edu-visual lv2-reveal">
              <span className="lv2-edu-visual-icon">🎬</span>
              <div className="lv2-edu-visual-title">{t.edu.visual_title}</div>
              <p className="lv2-edu-visual-text">{t.edu.visual_text_1}</p>
              <p className="lv2-edu-visual-text" style={{ marginTop: 14 }}>{t.edu.visual_text_2}</p>
              <span className="lv2-edu-badge">{t.edu.badge}</span>
            </div>
          </div>
        </div>
      </section>

      {/* NFT */}
      <section className="lv2-nft" id="nft">
        <div className="lv2-nft-inner">
          <span className="lv2-label lv2-reveal" style={{ display: "block" }}>{t.nft.label}</span>
          <h2 className="lv2-section-title lv2-reveal">
            {t.nft.title_1}<span className="lv2-accent">{t.nft.title_accent}</span>
          </h2>
          <p className="lv2-section-sub lv2-reveal">{t.nft.sub}</p>

          <div className="lv2-nft-grid">
            {t.nft.cards.map((c, i) => (
              <div className={`lv2-nft-card ${c.tier} lv2-reveal`} key={i}>
                <div className="lv2-nft-glow" />
                <span className="lv2-nft-emoji">{c.emoji}</span>
                <div className="lv2-nft-name">{c.name}</div>
                <div className="lv2-nft-price"><strong>{c.price}</strong></div>
                <span className="lv2-nft-rarity">{c.rarity}</span>
              </div>
            ))}
          </div>

          <p className="lv2-nft-footnote lv2-reveal">{t.nft.footnote}</p>
        </div>
      </section>

      {/* VIRAL */}
      <section className="lv2-viral" id="viral">
        <div className="lv2-viral-inner">
          <div className="lv2-viral-title lv2-reveal">
            <span className="lv2-g">{t.viral.l1}</span><br />
            <span className="lv2-o">{t.viral.l2}</span><br />
            <span>{t.viral.l3}</span><br />
            <span className="lv2-g">{t.viral.l4}</span>
          </div>

          <div className="lv2-viral-stats lv2-reveal">
            {t.viral.stats.map((s, i) => (
              <div className="lv2-viral-stat" key={i}>
                <span className={`lv2-vs-num ${s.color}`}>{s.num}</span>
                <span className="lv2-vs-label">{s.label}</span>
              </div>
            ))}
          </div>

          <Link to={registerHref} className="lv2-btn-primary" style={{ fontSize: 10, padding: "18px 40px" }} data-testid="lv2-viral-cta">
            {t.viral.cta}
          </Link>
        </div>
      </section>

      {/* ECOSYSTEM */}
      <section className="lv2-eco" id="ecosystem">
        <div className="lv2-eco-inner">
          <span className="lv2-label lv2-reveal" style={{ display: "block" }}>{t.eco.label}</span>
          <h2 className="lv2-section-title lv2-reveal">
            {t.eco.title_1}<span className="lv2-accent">{t.eco.title_accent}</span>
          </h2>
          <p className="lv2-section-sub lv2-reveal">{t.eco.sub}</p>

          <div className="lv2-eco-chain lv2-reveal">
            {t.eco.nodes.map((n, i) => (
              <React.Fragment key={i}>
                <div className="lv2-eco-node">
                  <span className="lv2-eco-node-icon">{n.icon}</span>
                  <div className="lv2-eco-node-name">{n.name}</div>
                  <div className="lv2-eco-node-desc">{n.desc}</div>
                </div>
                {i < t.eco.nodes.length - 1 && <span className="lv2-eco-arrow">→</span>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="lv2-cta-sec" id="cta">
        <div className="lv2-cta-inner">
          <span className="lv2-cta-bomb">💣</span>
          <h2 className="lv2-cta-title lv2-reveal">
            {t.cta.title_1}<br />
            <span className="lv2-accent-g">{t.cta.title_accent}</span><br />
            {t.cta.title_2}
          </h2>
          <p className="lv2-cta-sub lv2-reveal">
            {t.cta.sub_1}<strong>{t.cta.sub_strong}</strong>{t.cta.sub_2}
          </p>
          <div className="lv2-cta-btns lv2-reveal">
            <Link to={registerHref} className="lv2-btn-primary" style={{ fontSize: 10, padding: "20px 40px" }} data-testid="lv2-final-cta">
              {t.cta.btn_primary}
            </Link>
            <a href="#" className="lv2-btn-secondary" style={{ fontSize: 9, padding: "20px 30px" }} onClick={(e) => e.preventDefault()}>{t.cta.btn_app}</a>
            <a href="#" className="lv2-btn-secondary" style={{ fontSize: 9, padding: "20px 30px" }} onClick={(e) => e.preventDefault()}>{t.cta.btn_play}</a>
          </div>
          <p className="lv2-cta-note lv2-reveal">
            {t.cta.note_1}<a href="#earn">{t.cta.note_link}</a>
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="lv2-footer">
        <div className="lv2-footer-inner">
          <div className="lv2-footer-top">
            <div className="lv2-footer-brand">
              <span className="lv2-logo">💣 CRYPTOBOOM</span>
              <p>{t.footer.tagline}</p>
            </div>
            {[t.footer.col1, t.footer.col2, t.footer.col3].map((col, i) => (
              <div className="lv2-footer-col" key={i}>
                <h4>{col.title}</h4>
                <ul>
                  {col.items.map((item, j) => (
                    <li key={j}><a href="#" onClick={(e) => e.preventDefault()}>{item}</a></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="lv2-footer-bottom">
            <p>{t.footer.copyright}</p>
            <div className="lv2-footer-legal" style={{ display: "flex", gap: 14, fontSize: 12 }}>
              <Link to="/terms" data-testid="lv2-footer-terms" style={{ color: "var(--lv2-muted)" }}>Terms</Link>
              <Link to="/privacy" data-testid="lv2-footer-privacy" style={{ color: "var(--lv2-muted)" }}>Privacy</Link>
            </div>
            <div className="lv2-social-links">
              <a href="#" className="lv2-social-link" onClick={(e) => e.preventDefault()}>✈️</a>
              <a href="#" className="lv2-social-link" onClick={(e) => e.preventDefault()}>📸</a>
              <a href="#" className="lv2-social-link" onClick={(e) => e.preventDefault()}>🐦</a>
              <a href="#" className="lv2-social-link" onClick={(e) => e.preventDefault()}>▶️</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
